// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-2 — asp-pot public/modal.js `showAlert(msg, type)`: toasts stacked top-right, max width
// 380px, radius 10px, gone after 5s, "×" to close, with the four ASP colourings (spec §0.5).
// The overlays call `showPotAlert(...)`; the /pot pages themselves use the kit's useToast so the
// one toast provider of the app keeps its queue rules. `showConfirm` of modal.js is the kit's
// ConfirmDialog — nothing here opens a browser dialog.
import { useEffect, useState } from 'react'
import { POT } from '../potTheme'

export type PotAlertType = 'success' | 'error' | 'warning' | 'info'
interface PotAlert { id: number; message: string; type: PotAlertType }

const STYLE: Record<PotAlertType, { bg: string; fg: string; border: string; icon: string }> = {
  success: { bg: '#EDF7ED', fg: '#107C10', border: '#C0DD97', icon: '✅' },
  error: { bg: '#FEF0F0', fg: '#A4262C', border: '#F5C6C6', icon: '❌' },
  warning: { bg: '#FFF7ED', fg: '#92400E', border: '#F5D5A0', icon: '⚠️' },
  info: { bg: '#EBF5FF', fg: '#0078D4', border: '#93C5FD', icon: 'ℹ️' },
}
const AUTO_HIDE_MS = 5000
const listeners = new Set<(a: PotAlert) => void>()
let nextId = 1

/** modal.js `showAlert`: callable from anywhere under the POT shell (overlays, non-React code). */
export function showPotAlert(message: string, type: PotAlertType = 'info'): void {
  const a: PotAlert = { id: nextId++, message, type }
  listeners.forEach(l => l(a))
}

export default function PotToasts() {
  const [items, setItems] = useState<PotAlert[]>([])
  useEffect(() => {
    const on = (a: PotAlert) => {
      setItems(prev => prev.some(x => x.message === a.message && x.type === a.type) ? prev : [...prev, a])
      setTimeout(() => setItems(prev => prev.filter(x => x.id !== a.id)), AUTO_HIDE_MS)
    }
    listeners.add(on)
    return () => { listeners.delete(on) }
  }, [])
  if (items.length === 0) return null
  return (
    <div aria-live="polite" style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 380 }}>
      {items.map(t => {
        const s = STYLE[t.type]
        return (
          <div key={t.id} role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, fontSize: 13, fontFamily: POT.font, boxShadow: '0 4px 16px rgba(0,0,0,.15)', background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}>
            <span aria-hidden>{s.icon}</span>
            <span style={{ whiteSpace: 'pre-wrap' }}>{t.message}</span>
            <button type="button" aria-label="Close" onClick={() => setItems(prev => prev.filter(x => x.id !== t.id))}
              style={{ cursor: 'pointer', marginLeft: 'auto', fontSize: 18, opacity: .6, background: 'none', border: 'none', color: 'inherit', padding: 0, lineHeight: 1 }}>×</button>
          </div>
        )
      })}
    </div>
  )
}
